/**
 * LASER POINTER, INDUSTRIAL — Lumen Lance (doc 05 §4). 1×1, 55 alloy, 160 HP,
 * 12 dmg/s ramping to 2×, range 8, 5 watts while firing.
 *
 * "The cat is 40 meters long and made of teeth."
 *
 * Silhouette family: TRIPOD LENS. Three splayed legs holding a flat lens head
 * high and forward — the only airy, open-legged shape in the tower class, which
 * is what separates it from Stabby's solid mast at the black-shape test. The
 * head is a disc seen edge-on: wide across, one pixel thin at the rim.
 *
 * Signature: warm red aperture ring on the head; the BEAM itself is cold white
 * at the effect layer, because effects are not signatures (doc 10).
 *
 * Upgrade motifs (doc 05 3×3):
 *   A · DEATH STARE   a focusing collar that stacks into a long snout
 *   B · DISCO BALL    prisms bud off the head, ending in a four-way splitter
 *   C · FLASHLIGHT    a lamp housing that grows into a floodlight hood
 */

import { facingOffset, tiersOf, type BuildContext, type EntityDraw } from '../entity.ts';
import { rampFor, sigRamp, signatureColor, SIGNATURE_TOWER } from '../materials.ts';
import { isoCylinder, isoDiamondRim, isoPrism, linePx, type EntityCanvas } from '../../render/primitives.ts';

export const drawLaser: EntityDraw = (c: EntityCanvas, ctx: BuildContext): void => {
  const r = rampFor(ctx.depth);
  const sig = sigRamp(SIGNATURE_TOWER);
  const lamp = signatureColor(sig, ctx.state);
  const up = tiersOf(ctx);
  const aiming = ctx.state === 'tracking' || ctx.state === 'firing';

  // --- footing: a small hub, three splayed legs ------------------------------
  isoPrism(c, { n: 1, inset: 5, height: 2, ramp: r });
  const legTop = 13;
  for (const [lx, ly] of [
    [-7, 3],
    [7, 3],
    [0, -4],
  ] as Array<[number, number]>) {
    linePx(c.s, c.ax + lx, c.ay + ly, c.ax, c.ay - legTop, r.left, 2);
    c.s.px(c.ax + lx, c.ay + ly, r.deep);
  }
  // collar where the legs meet
  const hubTop = isoCylinder(c, { rx: 4, height: 3, lift: legTop, ramp: r });

  // --- the head: a lens disc, seen edge-on, tilted toward the target ---------
  const dir = facingOffset(ctx.facing, 6);
  const droop = aiming ? 0 : 2; // parked heads look down
  const hx = c.ax + Math.round(dir.x / 2);
  const hy = hubTop + 1 + Math.round(dir.y / 3) + droop;
  isoDiamondRim(c.s, hx, hy, 5, r.edge);
  c.s.fillRect(hx - 4, hy - 2, 9, 4, r.left);
  c.s.fillRect(hx - 4, hy - 2, 9, 1, r.edge);
  c.s.fillRect(hx - 2, hy - 1, 5, 2, r.deep); // the lens itself

  // --- A · DEATH STARE: a focusing snout, one collar per tier ----------------
  for (let k = 0; k < up.a; k++) {
    const ox = Math.round((dir.x / 6) * (k + 1));
    const oy = Math.round((dir.y / 8) * (k + 1));
    c.s.fillRect(hx + ox - 3, hy + oy - 2, 7, 4, r.left);
    c.s.fillRect(hx + ox - 3, hy + oy - 2, 7, 1, r.edge);
  }
  if (up.a >= 3) {
    // "Unblinking": the snout never closes — a long barrel out front
    const far = facingOffset(ctx.facing, 13);
    const tx = hx + far.x;
    const ty = hy + Math.round(far.y / 2);
    linePx(c.s, hx, hy, tx, ty, r.left, 5);
    linePx(c.s, hx, hy - 1, tx, ty - 1, r.edge, 1);
    c.s.fillRect(tx - 3, ty - 3, 7, 6, r.edge);
    c.s.fillRect(tx - 2, ty - 2, 5, 4, r.deep);
  }

  // --- B · DISCO BALL: prisms bud off the head ------------------------------
  if (up.b >= 1) {
    for (const side of [-1, 1]) c.s.fillRect(hx + side * 6 - 1, hy - 1, 3, 3, r.left);
  }
  if (up.b >= 2) {
    c.s.fillRect(hx - 1, hy - 5, 3, 3, r.left);
    c.s.px(hx, hy - 6, r.edge);
  }
  if (up.b >= 3) {
    // "Rave Cave": a four-armed splitter crowns the head
    for (const [dx, dy] of [
      [-8, 0],
      [8, 0],
      [0, -7],
      [0, 4],
    ] as Array<[number, number]>) {
      c.s.fillRect(hx + dx - 1, hy + dy - 1, 3, 3, r.edge);
      linePx(c.s, hx, hy, hx + dx, hy + dy, r.left, 1);
    }
  }

  // --- C · FLASHLIGHT OF TRUTH: a lamp that becomes a floodlight ------------
  if (up.c >= 1) {
    isoCylinder(c, { rx: 2, height: 3, lift: legTop - 4, dx: -6, dy: 2, ramp: r });
    c.s.px(c.ax - 6, c.ay - legTop + 2, lamp ?? r.edge);
  }
  if (up.c >= 2) {
    isoCylinder(c, { rx: 2, height: 3, lift: legTop - 4, dx: 6, dy: 2, ramp: r });
    c.s.fillRect(c.ax - 7, c.ay - legTop + 5, 15, 1, r.edge);
  }
  if (up.c >= 3) {
    // "Lights On, Nobody Home": a wide hood over the whole rig
    c.s.fillRect(c.ax - 9, c.ay - legTop - 1, 19, 2, r.left);
    c.s.fillRect(c.ax - 9, c.ay - legTop - 2, 19, 1, r.edge);
    for (const dx of [-8, -4, 0, 4, 8]) c.s.px(c.ax + dx, c.ay - legTop + 1, sig.on);
  }

  // --- signature + states ----------------------------------------------------
  if (lamp !== null) c.s.fillRect(hx - 1, hy + 2, 3, 1, lamp);
  if (ctx.state === 'firing') {
    // the aperture is white-hot and the first pixels of the beam show
    c.s.fillRect(hx - 2, hy - 1, 5, 2, sig.hot, 'set');
    const bx = hx + Math.round(dir.x / 2);
    const by = hy + Math.round(dir.y / 3);
    linePx(c.s, hx, hy, bx, by, sig.halo, 1);
  } else if (ctx.state === 'tracking') {
    c.s.fillRect(hx - 1, hy - 1, 3, 2, sig.on, 'set');
  }
  if (ctx.state === 'dry') {
    // no watts: the lens goes dark and the head hangs
    c.s.fillRect(hx - 3, hy - 1, 7, 2, r.deep, 'set');
    c.s.fillRect(c.ax - 4, c.ay - legTop + 4, 9, 1, r.deep);
  }
  if (ctx.state === 'selected') isoDiamondRim(c.s, c.ax, c.ay - 2, 6, r.edge);
};
